import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { useGlobalFilters } from '../components/GlobalFilterContext';
import { API_BASE } from '../../config';

// ============================================================
// TYPES — mirrors backend ChinaDependencyResponse
// ============================================================
interface ChinaDependencyProduct {
  id: number;
  ndc_code: string;
  product_name: string;
  generic_name: string | null;
  therapeutic_category: string | null;
  manufacturer_name: string | null;
  dependency_type: string;
  made_in_america_score: number | null;
  supply_chain_risk: string | null;
}

interface TherapeuticClassRow {
  therapeutic_class: string;
  total_products: number;
  china_dependent_count: number;
  china_dependent_pct: number;
}

interface CCPManufacturer {
  id: number;
  name: string;
  country: string | null;
  ccp_flag_source: string | null;
  ccp_flag_note: string | null;
  product_count: number;
  match_count: number;
}

interface ChinaDependencyData {
  total_products: number;
  china_coo_count: number;
  china_coo_pct: number;
  china_api_source_count: number;
  china_api_source_pct: number;
  china_finished_goods_count: number;
  china_finished_goods_pct: number;
  china_any_count: number;
  china_any_pct: number;
  ccp_connected_manufacturer_count: number;
  ccp_connected_product_count: number;
  ccp_connected_product_pct: number;
  ccp_china_api_count: number;
  therapeutic_class_breakdown: TherapeuticClassRow[];
  ccp_connected_manufacturers: CCPManufacturer[];
  risk_breakdown: Record<string, number>;
  top_china_dependent_products: ChinaDependencyProduct[];
}

// ============================================================
// PROPS
// ============================================================
interface ChinaDependencyPageProps {
  onSelectProduct: (id: number) => void;
  SectionHeader: React.ComponentType<{ children: React.ReactNode; sub?: string }>;
  ScoreRing: React.ComponentType<{ score: number; size?: number; strokeWidth?: number; color?: string; label?: string }>;
  StatCard: React.ComponentType<{ title: string; value: string | number; subtitle?: string; color?: string; icon?: React.ReactNode; accent?: string }>;
  RiskBadge: React.ComponentType<{ risk: string }>;
  IconAlert: React.ComponentType;
  IconShield: React.ComponentType;
  IconGlobe: React.ComponentType;
  IconSpinner: React.ComponentType;
}

// ============================================================
// China Dependency Dashboard — Issue #78 / B3.4
// ============================================================
export default function ChinaDependencyPage({
  onSelectProduct,
  SectionHeader,
  ScoreRing,
  StatCard,
  RiskBadge,
  IconAlert,
  IconShield,
  IconGlobe,
  IconSpinner,
}: ChinaDependencyPageProps) {
  const { T, getRiskColor } = useTheme();
  const navigate = useNavigate();

  // State
  const [data, setData] = useState<ChinaDependencyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [productFilter, setProductFilter] = useState('');
  const [productSort, setProductSort] = useState<'name' | 'ndc' | 'manufacturer' | 'dependency' | 'risk'>('risk');
  const [productSortDir, setProductSortDir] = useState<'asc' | 'desc'>('asc');
  const [tcSort, setTcSort] = useState<'therapeutic_class' | 'total_products' | 'china_dependent_count' | 'china_dependent_pct' | 'exposure'>('china_dependent_pct');
  const [tcSortDir, setTcSortDir] = useState<'asc' | 'desc'>('desc');
  const [tcSearch, setTcSearch] = useState('');
  const [activeSection, setActiveSection] = useState<'drugs' | 'apis' | 'therapeutic' | 'ccp' | null>(null);
  const [tcFilter, setTcFilter] = useState<string>('');
  const [ccpSearch, setCcpSearch] = useState('');
  const [apiSearch, setApiSearch] = useState('');

  // Server-side paginated china-dependent products
  interface ChinaProduct {
    id: number;
    ndc_code: string;
    product_name: string;
    generic_name: string | null;
    strength: string | null;
    manufacturer: string | null;
    country_of_origin: string | null;
    coo_scoring_score: number | null;
    coo_scoring_tier: string | null;
    supply_chain_risk: string;
    last_updated: string | null;
    shortage_status: string;
  }
  const [chinaProducts, setChinaProducts] = useState<ChinaProduct[]>([]);
  const [chinaProductsTotal, setChinaProductsTotal] = useState(0);
  const [chinaProductsLoading, setChinaProductsLoading] = useState(false);
  const [cpPage, setCpPage] = useState(1);
  const [cpSearch, setCpSearch] = useState('');
  const [cpSortField, setCpSortField] = useState('name');
  const [cpSortDir, setCpSortDir] = useState<'asc' | 'desc'>('asc');
  const cpPageSize = 25;

  // Responsive
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth <= 1024;

  const { filters: globalFilters } = useGlobalFilters();

  // Build global filter query string
  const globalFilterQS = (() => {
    const p = new URLSearchParams();
    if (globalFilters.cooConfidence.length > 0) p.set('coo_confidence', globalFilters.cooConfidence.join(','));
    if (globalFilters.country.length > 0) p.set('country', globalFilters.country.join(','));
    return p.toString();
  })();

  // Fetch data on mount and when global filters change
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = globalFilterQS
          ? `${API_BASE}/metasupply/geopolitical/china-dependency?${globalFilterQS}`
          : `${API_BASE}/metasupply/geopolitical/china-dependency`;
        const res = await fetch(url, {
          credentials: 'include',
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.detail || `Failed to load data (${res.status})`);
        }
        const json: ChinaDependencyData = await res.json();
        setData(json);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load China dependency data.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [globalFilterQS]);

  // Fetch china-dependent products (server-side pagination)
  const fetchChinaProducts = useCallback(async () => {
    setChinaProductsLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(cpPageSize),
        offset: String((cpPage - 1) * cpPageSize),
        sort_field: cpSortField,
        sort_dir: cpSortDir,
      });
      if (cpSearch.trim()) params.set('search', cpSearch.trim());
      // Apply global filters
      if (globalFilters.cooConfidence.length > 0) params.set('coo_confidence', globalFilters.cooConfidence.join(','));
      if (globalFilters.country.length > 0) params.set('country', globalFilters.country.join(','));
      const res = await fetch(`${API_BASE}/metasupply/products/china-dependent?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setChinaProducts(json.products || []);
      setChinaProductsTotal(json.total || 0);
    } catch {
      setChinaProducts([]);
      setChinaProductsTotal(0);
    } finally {
      setChinaProductsLoading(false);
    }
  }, [cpPage, cpSearch, cpSortField, cpSortDir, globalFilters.cooConfidence, globalFilters.country]);

  useEffect(() => { fetchChinaProducts(); }, [fetchChinaProducts]);

  const cpTotalPages = Math.ceil(chinaProductsTotal / cpPageSize);

  const handleCpSort = (field: string) => {
    if (cpSortField === field) {
      setCpSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setCpSortField(field);
      setCpSortDir('asc');
    }
    setCpPage(1);
  };

  const CpSortIcon = ({ field }: { field: string }) => (
    <span style={{ marginLeft: 4, fontSize: 10, opacity: cpSortField === field ? 1 : 0.3 }}>
      {cpSortField === field ? (cpSortDir === 'asc' ? '▲' : '▼') : '⇕'}
    </span>
  );

  // ── Helpers ──────────────────────────────────────────────────
  const depTypeLabel = (type: string): string => {
    switch (type) {
      case 'api_source': return 'API Sourcing';
      case 'finished_goods': return 'Finished Goods';
      case 'both': return 'API + Finished Goods';
      case 'coo_only': return 'Country of Origin';
      default: return type;
    }
  };

  const depTypeColor = (type: string): string => {
    switch (type) {
      case 'api_source': return T.danger;
      case 'finished_goods': return T.warning;
      case 'both': return '#7C3AED';
      case 'coo_only': return T.navyLight;
      default: return T.textLight;
    }
  };

  // Filter + sort products
  const getFilteredProducts = (): ChinaDependencyProduct[] => {
    if (!data) return [];
    let products = [...data.top_china_dependent_products];

    if (productFilter.trim()) {
      const q = productFilter.toLowerCase();
      products = products.filter(
        p =>
          p.product_name.toLowerCase().includes(q) ||
          (p.generic_name || '').toLowerCase().includes(q) ||
          (p.manufacturer_name || '').toLowerCase().includes(q) ||
          p.ndc_code.toLowerCase().includes(q)
      );
    }

    products.sort((a, b) => {
      let cmp = 0;
      switch (productSort) {
        case 'risk': {
          const order: Record<string, number> = { high: 0, medium: 1, low: 2 };
          cmp = (order[a.supply_chain_risk || ''] ?? 3) - (order[b.supply_chain_risk || ''] ?? 3);
          break;
        }
        case 'ndc':
          cmp = a.ndc_code.localeCompare(b.ndc_code);
          break;
        case 'manufacturer':
          cmp = (a.manufacturer_name || '').localeCompare(b.manufacturer_name || '');
          break;
        case 'dependency':
          cmp = a.dependency_type.localeCompare(b.dependency_type);
          break;
        case 'name':
        default:
          cmp = a.product_name.localeCompare(b.product_name);
      }
      return productSortDir === 'desc' ? -cmp : cmp;
    });

    return products;
  };

  // ── Loading state ────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 16 }}>
        <IconSpinner />
        <span style={{ fontSize: 14, color: T.textMid }}>Loading China dependency data...</span>
      </div>
    );
  }

  // ── Error state ──────────────────────────────────────────────
  if (error || !data) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <SectionHeader sub="Global supply chain intelligence">China Dependency Dashboard</SectionHeader>
        <div style={{
          background: T.dangerBg, border: `1px solid #FECACA`, borderRadius: T.radius,
          padding: 32, textAlign: 'center',
        }}>
          <IconAlert />
          <h3 style={{ fontSize: 18, fontWeight: 700, color: T.danger, marginTop: 12 }}>
            Failed to Load Data
          </h3>
          <p style={{ fontSize: 14, color: T.textMid, marginTop: 8 }}>
            {error || 'An unexpected error occurred.'}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 16, padding: '10px 24px', borderRadius: T.radiusSm,
              border: 'none', background: T.navy, color: T.white,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Breakdown chart data ─────────────────────────────────────
  const breakdownItems = [
    { label: 'API Sourcing', value: data.risk_breakdown.api_source_only || 0, color: T.danger },
    { label: 'Finished Goods', value: data.risk_breakdown.finished_goods_only || 0, color: T.warning },
    { label: 'Both (API + FG)', value: data.risk_breakdown.both || 0, color: '#7C3AED' },
    { label: 'COO Only', value: data.risk_breakdown.coo_only || 0, color: T.navyLight },
  ];
  const breakdownTotal = breakdownItems.reduce((s, i) => s + i.value, 0);

  const filteredProducts = getFilteredProducts();

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

      {/* ── Hero Section ──────────────────────────────────────── */}
      <div style={{
        position: 'relative',
        background: T.offWhite, border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        padding: isMobile ? '28px 20px' : '40px 44px',
        overflow: 'hidden',
      }}>
        {/* Background decoration */}
        <div style={{
          position: 'absolute', right: isMobile ? -40 : 40, top: '50%',
          transform: 'translateY(-50%)', opacity: 0.15,
        }}>
          <img src="/ccp-logo.png" alt="CCP" width={126} height={126} style={{ objectFit: 'contain' }} />
        </div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{
            fontSize: isMobile ? 24 : 32, fontWeight: 800,
            color: T.textDark, letterSpacing: '-0.02em',
            lineHeight: 1.2, marginBottom: 8,
            fontFamily: "'Playfair Display', Georgia, serif",
          }}>
            China/Chinese Communist Party
          </h1>
          <p style={{
            fontSize: 14, color: T.textMid,
            maxWidth: 600, lineHeight: 1.6,
          }}>
            Pharmaceutical risk exposure to China-sourced API&apos;s, finished goods, and CCP-connected manufacturers.
          </p>
        </div>
      </div>

      {/* ── 4 Navigation Tiles ──────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: 16,
      }}>
        {([
          { id: 'drugs' as const, title: 'Drugs', value: `${data.china_any_count.toLocaleString()}`, sub: `${data.china_any_pct}% of portfolio`, color: T.danger, icon: <IconGlobe /> },
          { id: 'apis' as const, title: "API's", value: `${data.ccp_china_api_count.toLocaleString()}`, sub: `CCP-flagged, China API dep.`, color: T.warning, icon: <IconShield /> },
          { id: 'therapeutic' as const, title: 'Therapeutic Vulnerability', value: `${data.therapeutic_class_breakdown.length}`, sub: 'Classes exposed', color: T.navyLight, icon: <IconShield /> },
          { id: 'ccp' as const, title: 'CCP Connection', value: `${data.ccp_connected_manufacturer_count}`, sub: `${data.ccp_connected_product_count} products`, color: '#7C3AED', icon: <IconAlert /> },
        ]).map(tile => {
          const isActive = activeSection === tile.id;
          return (
            <div
              key={tile.id}
              onClick={() => setActiveSection(isActive ? null : tile.id)}
              style={{
                padding: '18px 16px',
                background: isActive ? tile.color : T.white,
                border: `1.5px solid ${isActive ? tile.color : T.border}`,
                borderRadius: T.radiusSm,
                cursor: 'pointer',
                transition: 'all 0.15s',
                boxShadow: isActive ? `0 2px 8px ${tile.color}30` : T.shadow,
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 24, fontWeight: 800, color: isActive ? T.white : tile.color, lineHeight: 1 }}>
                {tile.value}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: isActive ? 'rgba(255,255,255,0.9)' : T.textDark, marginTop: 6 }}>
                {tile.title}
              </div>
              <div style={{ fontSize: 11, color: isActive ? 'rgba(255,255,255,0.7)' : T.textLight, marginTop: 2 }}>
                {tile.sub}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── API's Detail Section ──────────────────────────────── */}
      {activeSection === 'apis' && (
        <div style={{
          background: T.white, border: `1px solid ${T.border}`,
          borderRadius: T.radius, padding: isMobile ? 20 : 28,
          boxShadow: T.shadow,
        }}>
          <SectionHeader sub={`${data.ccp_china_api_count.toLocaleString()} CCP-flagged products with China-dependent APIs`}>
            China API Source Detail
          </SectionHeader>

          <div style={{ fontSize: 13, color: T.textMid, marginTop: 12, marginBottom: 16 }}>
            Products whose API molecule family has {'\u2265'}50% China sourcing (based on US import trade flows via UN Comtrade).
            Click a row to view the full product detail.
          </div>

          {/* Search field */}
          <div style={{ marginBottom: 14 }}>
            <input
              type="text"
              placeholder="Search by drug name, generic name, or manufacturer..."
              value={apiSearch}
              onChange={e => setApiSearch(e.target.value)}
              style={{
                width: '100%', maxWidth: 400,
                padding: '8px 14px', borderRadius: T.radiusSm,
                border: `1px solid ${T.border}`, fontSize: 13,
                color: T.textDark, background: T.offWhite,
                outline: 'none',
              }}
              onFocus={e => { e.currentTarget.style.borderColor = T.navy; }}
              onBlur={e => { e.currentTarget.style.borderColor = T.border; }}
            />
          </div>

          {/* Reuse the same china-dependent products table already loaded */}
          {chinaProductsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><IconSpinner /></div>
          ) : chinaProducts.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: T.textLight, fontSize: 14 }}>
              No China API-dependent products found.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: T.offWhite }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>Drug</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>Generic Name</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>Manufacturer</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>COO</th>
                    <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {chinaProducts
                    .filter(p => {
                      if (!apiSearch.trim()) return true;
                      const q = apiSearch.toLowerCase();
                      return (p.product_name || '').toLowerCase().includes(q)
                        || (p.generic_name || '').toLowerCase().includes(q)
                        || (p.manufacturer || '').toLowerCase().includes(q);
                    })
                    .slice(0, 25).map((p, idx) => (
                    <tr
                      key={p.id}
                      onClick={() => onSelectProduct(p.id)}
                      style={{
                        cursor: 'pointer',
                        borderBottom: idx < Math.min(chinaProducts.length, 25) - 1 ? `1px solid ${T.borderLight}` : 'none',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = T.offWhite; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = T.white; }}
                    >
                      <td style={{ padding: '12px 16px', maxWidth: 240 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.textDark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.product_name}
                        </div>
                        {p.strength && <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>{p.strength}</div>}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: T.textMid }}>
                        {p.generic_name || '\u2014'}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: T.textMid }}>
                        {p.manufacturer || 'Unknown'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          fontSize: 13, fontWeight: 600,
                          color: (() => {
                            const coo = (p.country_of_origin || '').toLowerCase();
                            if (coo.includes('united states') || coo === 'usa' || coo === 'u.s.a.' || coo === 'u.s.') return T.success;
                            if (coo.includes('china') || coo.includes('prc')) return T.danger;
                            if (coo.includes('india')) return T.warning;
                            return T.textMid;
                          })(),
                        }}>
                          {p.country_of_origin || 'Unknown'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <RiskBadge risk={p.supply_chain_risk} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {chinaProducts.length > 0 && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <button
                onClick={() => navigate('/apis?expand=china')}
                style={{
                  padding: '10px 24px', borderRadius: T.radiusSm,
                  border: 'none', background: T.navy, color: T.white,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                View Full API Intelligence Page
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Therapeutic Vulnerability Detail Section ───────────── */}
      {activeSection === 'therapeutic' && (
        <>
          {/* Therapeutic class filter pills */}
          {data.therapeutic_class_breakdown.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <button
                onClick={() => setTcFilter('')}
                style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${!tcFilter ? T.navy : T.border}`,
                  background: !tcFilter ? T.navy : T.white,
                  color: !tcFilter ? T.white : T.textMid,
                  transition: 'all 0.15s',
                }}
              >All Classes</button>
              {data.therapeutic_class_breakdown
                .sort((a, b) => b.china_dependent_pct - a.china_dependent_pct)
                .slice(0, 15)
                .map(tc => (
                  <button
                    key={tc.therapeutic_class}
                    onClick={() => setTcFilter(tcFilter === tc.therapeutic_class ? '' : tc.therapeutic_class)}
                    style={{
                      padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      border: `1px solid ${tcFilter === tc.therapeutic_class ? T.navy : T.border}`,
                      background: tcFilter === tc.therapeutic_class ? T.navy : T.white,
                      color: tcFilter === tc.therapeutic_class ? T.white : T.textMid,
                      transition: 'all 0.15s',
                    }}
                  >{tc.therapeutic_class} ({tc.china_dependent_pct}%)</button>
                ))}
            </div>
          )}

      {/* ── Therapeutic Class Vulnerability Table ─────────────── */}
      <div style={{
        background: T.white, border: `1px solid ${T.border}`,
        borderRadius: T.radius, padding: isMobile ? 20 : 28,
        boxShadow: T.shadow,
      }}>
        <SectionHeader sub="Therapeutic classes with highest China supply chain exposure">
          Therapeutic Class Vulnerability
        </SectionHeader>

        <div style={{ padding: isMobile ? '0' : '0', marginTop: 12, marginBottom: 8 }}>
          <input
            type="text"
            placeholder="Search therapeutic classes..."
            value={tcSearch}
            onChange={e => setTcSearch(e.target.value)}
            style={{
              width: '100%', maxWidth: 320, padding: '8px 14px',
              borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
              fontSize: 13, color: T.textDark, outline: 'none',
              background: T.offWhite,
            }}
          />
        </div>

        {data.therapeutic_class_breakdown.length === 0 ? (
          <div style={{
            padding: 32, textAlign: 'center', color: T.textLight, fontSize: 14,
          }}>
            No therapeutic class dependency data available.
          </div>
        ) : (
          <div style={{ marginTop: 8, overflowX: 'auto' }}>
            <table style={{
              width: '100%', borderCollapse: 'collapse', fontSize: 13,
            }}>
              <thead>
                <tr>
                  {([['Therapeutic Class', 'therapeutic_class', 'left'], ['Total Products', 'total_products', 'center'], ['China Dependent', 'china_dependent_count', 'center'], ['% Dependent', 'china_dependent_pct', 'center'], ['Exposure', 'exposure', 'center']] as const).map(([label, key, align]) => (
                    <th key={key} onClick={() => {
                      if (tcSort === key) setTcSortDir(d => d === 'asc' ? 'desc' : 'asc');
                      else { setTcSort(key); setTcSortDir(key === 'therapeutic_class' ? 'asc' : 'desc'); }
                    }} style={{
                      textAlign: align as 'left' | 'center',
                      padding: '10px 12px',
                      fontSize: 11, fontWeight: 700, color: tcSort === key ? T.navy : T.textLight,
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                      borderBottom: `2px solid ${tcSort === key ? T.gold : T.border}`,
                      whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
                    }}>
                      {label} {tcSort === key ? (tcSortDir === 'asc' ? '▲' : '▼') : '⇕'}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...data.therapeutic_class_breakdown]
                  .filter(tc => (!tcSearch.trim() || tc.therapeutic_class.toLowerCase().includes(tcSearch.toLowerCase())) && (!tcFilter || tc.therapeutic_class === tcFilter))
                  .sort((a, b) => {
                    let cmp = 0;
                    switch (tcSort) {
                      case 'therapeutic_class': cmp = a.therapeutic_class.localeCompare(b.therapeutic_class); break;
                      case 'total_products': cmp = a.total_products - b.total_products; break;
                      case 'china_dependent_count': cmp = a.china_dependent_count - b.china_dependent_count; break;
                      case 'china_dependent_pct': cmp = a.china_dependent_pct - b.china_dependent_pct; break;
                      case 'exposure': cmp = a.china_dependent_pct - b.china_dependent_pct; break;
                      default: cmp = b.china_dependent_pct - a.china_dependent_pct;
                    }
                    return tcSortDir === 'desc' ? -cmp : cmp;
                  })
                  .map((tc, idx) => {
                  const exposureLevel = tc.china_dependent_pct >= 50 ? 'high' : tc.china_dependent_pct >= 25 ? 'medium' : 'low';
                  const riskColors = getRiskColor(exposureLevel);
                  return (
                    <tr
                      key={idx}
                      style={{
                        borderBottom: `1px solid ${T.borderLight}`,
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = T.offWhite; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                    >
                      <td style={{ padding: '12px 12px', fontWeight: 600, color: T.textDark, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {tc.therapeutic_class}
                      </td>
                      <td style={{ padding: '12px 12px', textAlign: 'center', color: T.textMid }}>
                        {tc.total_products}
                      </td>
                      <td style={{ padding: '12px 12px', textAlign: 'center', fontWeight: 700, color: T.danger }}>
                        {tc.china_dependent_count}
                      </td>
                      <td style={{ padding: '12px 12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                          <div style={{
                            width: 60, height: 6, borderRadius: 3,
                            background: T.border, overflow: 'hidden',
                          }}>
                            <div style={{
                              width: `${Math.min(tc.china_dependent_pct, 100)}%`,
                              height: '100%', borderRadius: 3,
                              background: riskColors.text,
                              transition: 'width 0.4s ease',
                            }} />
                          </div>
                          <span style={{ fontWeight: 600, color: riskColors.text, fontSize: 12 }}>
                            {tc.china_dependent_pct}%
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 12px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block', padding: '3px 10px',
                          borderRadius: 12, fontSize: 10, fontWeight: 700,
                          textTransform: 'uppercase', letterSpacing: '0.06em',
                          background: riskColors.bg, color: riskColors.text,
                          border: `1px solid ${riskColors.border}`,
                        }}>
                          {exposureLevel}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

        </>
      )}

      {/* ── CCP Connection Explorer (shown ABOVE products when CCP tile selected) ── */}
      {activeSection === 'ccp' && data.ccp_connected_manufacturers.length > 0 && (
        <div style={{
          background: T.white, border: `1px solid ${T.border}`,
          borderRadius: T.radius, padding: isMobile ? 20 : 28,
          boxShadow: T.shadow,
        }}>
          <SectionHeader>
            CCP Connection Explorer
          </SectionHeader>

          {/* Search field */}
          <div style={{ marginTop: 12, marginBottom: 14 }}>
            <input
              type="text"
              placeholder="Search CCP-connected manufacturers..."
              value={ccpSearch}
              onChange={e => setCcpSearch(e.target.value)}
              style={{
                width: '100%', maxWidth: 400,
                padding: '8px 14px', borderRadius: T.radiusSm,
                border: `1px solid ${T.border}`, fontSize: 13,
                color: T.textDark, background: T.offWhite,
                outline: 'none',
              }}
              onFocus={e => { e.currentTarget.style.borderColor = T.navy; }}
              onBlur={e => { e.currentTarget.style.borderColor = T.border; }}
            />
          </div>

          {/* Manufacturer Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: 14,
          }}>
            {data.ccp_connected_manufacturers
              .filter(mfr => {
                if (!ccpSearch.trim()) return true;
                const q = ccpSearch.toLowerCase();
                return (mfr.name || '').toLowerCase().includes(q)
                  || (mfr.country || '').toLowerCase().includes(q)
                  || (mfr.ccp_flag_note || '').toLowerCase().includes(q);
              })
              .map(mfr => (
              <div
                key={mfr.id}
                onClick={() => navigate(`/manufacturers/${mfr.id}?tab=ccp`)}
                style={{
                  padding: 16, borderRadius: T.radiusSm,
                  border: `1px solid ${T.border}`, background: T.offWhite,
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLDivElement).style.borderColor = '#7C3AED40';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = T.shadowMd;
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLDivElement).style.borderColor = T.border;
                  (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, color: T.textDark, fontSize: 14 }}>
                    {mfr.name}
                  </div>
                  <span style={{
                    padding: '2px 8px', borderRadius: 10,
                    background: '#7C3AED15', color: '#7C3AED',
                    fontSize: 10, fontWeight: 700,
                  }}>
                    CCP
                  </span>
                </div>
                {mfr.country && (
                  <div style={{ fontSize: 12, color: T.textMid, marginBottom: 6 }}>
                    {mfr.country}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
                  <span style={{ color: T.textMid }}>
                    <strong style={{ color: T.navy }}>{mfr.product_count}</strong> products
                  </span>
                  <span style={{ color: T.textMid }}>
                    <strong style={{ color: '#7C3AED' }}>{mfr.match_count}</strong> matches
                  </span>
                </div>
                {mfr.ccp_flag_note && (
                  <div style={{
                    marginTop: 8, fontSize: 11, color: T.textLight,
                    lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis',
                    display: '-webkit-box', WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}>
                    {mfr.ccp_flag_note}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── China-Dependent Products (server-side paginated, Drugs-page style) ── */}
      <div style={{
        background: T.white, border: `1px solid ${T.border}`,
        borderRadius: T.radius, padding: isMobile ? 20 : 28,
        boxShadow: T.shadow,
      }}>
        <SectionHeader sub={`${chinaProductsTotal.toLocaleString()} products with China supply chain exposure`}>
          China-Dependent Products
        </SectionHeader>

        {/* Search */}
        <div style={{ display: 'flex', gap: 12, marginTop: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search by name, NDC, or manufacturer..."
            value={cpSearch}
            onChange={e => { setCpSearch(e.target.value); setCpPage(1); }}
            style={{
              flex: 1, minWidth: 200, padding: '10px 14px',
              borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
              fontSize: 13, color: T.textDark, outline: 'none', background: T.offWhite,
            }}
          />
        </div>

        {chinaProductsLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><IconSpinner /></div>
        ) : chinaProducts.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: T.textLight, fontSize: 14 }}>
            {cpSearch ? 'No products match your search.' : 'No China-dependent products found.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: T.offWhite }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}`, cursor: 'pointer' }} onClick={() => handleCpSort('name')}>
                    Drug <CpSortIcon field="name" />
                  </th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}`, cursor: 'pointer' }} onClick={() => handleCpSort('manufacturer')}>
                    Manufacturer <CpSortIcon field="manufacturer" />
                  </th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>
                    COO
                  </th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>
                    COO Confidence
                  </th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}` }}>
                    Shortage
                  </th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}`, cursor: 'pointer' }} onClick={() => handleCpSort('risk')}>
                    Risk <CpSortIcon field="risk" />
                  </th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${T.border}`, cursor: 'pointer' }} onClick={() => handleCpSort('updated')}>
                    Updated <CpSortIcon field="updated" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {chinaProducts.map((p, idx) => {
                  const isActive = p.shortage_status && p.shortage_status !== 'None' && p.shortage_status !== 'none';
                  return (
                    <tr
                      key={p.id}
                      onClick={() => onSelectProduct(p.id)}
                      style={{
                        cursor: 'pointer',
                        borderBottom: idx < chinaProducts.length - 1 ? `1px solid ${T.borderLight}` : 'none',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = T.offWhite; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = T.white; }}
                    >
                      <td style={{ padding: '14px 16px', maxWidth: 280 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.textDark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.product_name}
                        </div>
                        {p.strength && (
                          <div style={{ fontSize: 12, color: T.textLight, marginTop: 2 }}>{p.strength}</div>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ fontSize: 13, color: T.textMid }}>{p.manufacturer || 'Unknown'}</span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{
                          fontSize: 13, fontWeight: 600,
                          color: (() => {
                            const coo = (p.country_of_origin || '').toLowerCase();
                            if (coo.includes('united states') || coo === 'usa' || coo === 'u.s.a.' || coo === 'u.s.') return T.success;
                            if (coo.includes('china') || coo.includes('prc')) return T.danger;
                            if (coo.includes('india')) return T.warning;
                            return T.textMid;
                          })(),
                        }}>
                          {p.country_of_origin || 'Unknown'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        {(!p.country_of_origin || p.country_of_origin === 'Non-US / Unknown' || p.country_of_origin === 'No Country Assigned') ? (
                          <span style={{ fontSize: 12, color: T.textLight, fontStyle: 'italic' }}>Unknown</span>
                        ) : p.coo_scoring_tier ? (() => {
                          const upper = p.coo_scoring_tier.toUpperCase();
                          const isHigh = upper === 'HIGH' || upper === 'NEAR_CERTAIN';
                          const isMod = upper === 'MODERATE' || upper === 'MODERATE_HIGH';
                          const label = isHigh ? 'High' : isMod ? 'Medium' : 'Low';
                          const color = isHigh ? '#22C55E' : isMod ? '#F59E0B' : '#EF4444';
                          const bg = isHigh ? '#22C55E18' : isMod ? '#F59E0B18' : '#EF444418';
                          return (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: bg, color, whiteSpace: 'nowrap' }}>
                              {label}
                            </span>
                          );
                        })() : (
                          <span style={{ fontSize: 12, color: T.textLight }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                          background: isActive ? T.warningBg : T.successBg,
                          color: isActive ? T.warning : T.success,
                          border: `1px solid ${isActive ? '#FDE68A' : '#BBF7D0'}`,
                        }}>
                          {isActive ? p.shortage_status : 'None'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <RiskBadge risk={p.supply_chain_risk} />
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ fontSize: 12, color: T.textLight }}>
                          {p.last_updated
                            ? new Date(p.last_updated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {cpTotalPages > 1 && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.border}`,
          }}>
            <span style={{ fontSize: 13, color: T.textMid }}>
              Showing {((cpPage - 1) * cpPageSize) + 1}–{Math.min(cpPage * cpPageSize, chinaProductsTotal)} of {chinaProductsTotal.toLocaleString()}
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                disabled={cpPage <= 1}
                onClick={() => setCpPage(1)}
                style={{
                  padding: '6px 10px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
                  background: T.white, color: cpPage <= 1 ? T.textLight : T.textDark, cursor: cpPage <= 1 ? 'default' : 'pointer',
                  fontSize: 12, fontWeight: 600,
                }}
              >«</button>
              <button
                disabled={cpPage <= 1}
                onClick={() => setCpPage(p => p - 1)}
                style={{
                  padding: '6px 10px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
                  background: T.white, color: cpPage <= 1 ? T.textLight : T.textDark, cursor: cpPage <= 1 ? 'default' : 'pointer',
                  fontSize: 12, fontWeight: 600,
                }}
              >‹</button>
              <span style={{ padding: '6px 12px', fontSize: 12, color: T.textMid, display: 'flex', alignItems: 'center' }}>
                Page {cpPage} of {cpTotalPages}
              </span>
              <button
                disabled={cpPage >= cpTotalPages}
                onClick={() => setCpPage(p => p + 1)}
                style={{
                  padding: '6px 10px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
                  background: T.white, color: cpPage >= cpTotalPages ? T.textLight : T.textDark, cursor: cpPage >= cpTotalPages ? 'default' : 'pointer',
                  fontSize: 12, fontWeight: 600,
                }}
              >›</button>
              <button
                disabled={cpPage >= cpTotalPages}
                onClick={() => setCpPage(cpTotalPages)}
                style={{
                  padding: '6px 10px', borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
                  background: T.white, color: cpPage >= cpTotalPages ? T.textLight : T.textDark, cursor: cpPage >= cpTotalPages ? 'default' : 'pointer',
                  fontSize: 12, fontWeight: 600,
                }}
              >»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
